import React, {useRef} from 'react';
import {Box, useDimensions} from '@chakra-ui/react';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import {companyFilterState, companyViewState} from '../nav';
import PortfolioListGridView from './gridView';
import PortfolioListListView from './listView';
import {htmlDecode} from '../../../../utils/htmlDecode';
import {AnimatePresence, motion} from 'framer-motion';
import {useParams} from 'react-router-dom';
import PortfolioDetail, {selectedCompany} from '../company-detail';
import Container from "../../../../components/Container";
import FullCrossPlus from "../../../../components/FullCrossPlus";

const ListWrapper = motion(Box);

const PortfolioList = ({companies}) => {
    const pageRef = useRef();
    const params = useParams();
    const view = useRecoilValue(companyViewState);
    const setSelectedCompany = useSetRecoilState(selectedCompany);
    const {technology, private_healthcare, investment_status, alumni, firstInvestment, featured} =
        useRecoilValue(companyFilterState);
    const dimen = useDimensions(pageRef, true);

    const count = Math.floor(dimen?.contentBox.height / 393) || 5;

    const isMobile = dimen?.contentBox.width < 768;
    const appliedTechnology = React.useCallback(
        (data) => {
            return technology
                ? data.filter((fid) => {
                    if (technology === 'All') {
                        return fid.parent_investment_category.some((i) => i.name === 'Technology')
                    }
                    const option = fid.investment_sectors?.find((item) => {
                        const name = item.name.toLowerCase();
                        const tech = technology.toLowerCase();
                        return htmlDecode(name) === htmlDecode(tech) || name.includes(tech);
                    });
                    if (!!option) {
                        return fid;
                    }
                })
                : data;
        },
        [technology],
    );

    const appliedPrivatehealcare = React.useCallback(
        (data) => {
            return private_healthcare
                ? data.filter((fid) => {
                    if (private_healthcare === 'All') {
                        return fid.parent_investment_category.some((i) => i.name === 'Private Healthcare')
                    }
                    const isPrivateHealcare = fid.parent_investment_category?.find(
                        (item) => item.name === 'Private Healthcare',
                    );

                    if (!!isPrivateHealcare) {
                        const option = fid.investment_sectors?.find((item) => {
                            const name = item.name.toLowerCase();
                            const tech = private_healthcare.toLowerCase();
                            return htmlDecode(name) === htmlDecode(tech) || name.includes(tech);
                        });
                        if (!!option) {
                            return fid;
                        }
                    }
                    return null;
                })
                : data;
        },
        [private_healthcare],
    );

    const appliedInvestmentStatus = React.useCallback(
        (data) => {
            return investment_status
                ? data.filter((fid) => {
                    const isInvestmentStatus = fid.investment_status?.find(
                        (item) => item.name === 'Active',
                    );

                    if (!!isInvestmentStatus) {
                        return fid;
                    }
                    return null;
                })
                : data;
        },
        [investment_status],
    );
    const appliedAlumni = React.useCallback(
        (data) => {
            return alumni
                ? data.filter((fid) => {
                    const isAlumni = fid.investment_status?.find(
                        (item) => item.name === 'Alumni',
                    );
                    if (!!isAlumni) {
                        return fid;
                    }
                    return null;
                })
                : data;
        },
        [alumni],
    );
    const appliedFirstFundedRound = React.useCallback(
        (data) => firstInvestment ? data.filter((a) => a.first_funded_round === firstInvestment) : data,
        [firstInvestment],
    );
    const appliedFeatured = React.useCallback(
        (data) => featured ? data.filter((a) => a.featured_flag) : data,
        [featured],
    );

    const options = React.useMemo(() => {
        return [
            appliedTechnology,
            appliedPrivatehealcare,
            appliedInvestmentStatus,
            appliedAlumni,
            appliedFirstFundedRound,
            appliedFeatured
        ].reduce((acc, item) => {
            if (acc) {
                return item(acc);
            }
            return item(companies);
        }, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [technology, private_healthcare, investment_status, alumni, firstInvestment, appliedFeatured, companies]);

    React.useEffect(() => {
        let time = setTimeout(() => {
            const index = options.length > 0 ? options?.findIndex((fid) => fid.slug === params.slug) : undefined;
            if (index >= 0) {
                setSelectedCompany(index);
            } else {
                setSelectedCompany(null);
            }
        }, 100);
        return () => {
            clearTimeout(time);
        };
    }, [setSelectedCompany, options, params]);

    return (
        <>
            <Box ref={pageRef}>
                {!isMobile && Array(count)
                    .fill('')
                    .map((_, key) => (
                        <FullCrossPlus index={key + 1} top={393 * (key + 1)} key={key}/>
                    ))}
                <Box borderX={'1px'} borderColor={'brandAlpha.200'}>
                    <Container>
                        <AnimatePresence>
                            {view === 'grid' ? (
                                <ListWrapper
                                    initial={{opacity: 0}}
                                    animate={{opacity: 1}}
                                    exit={{opacity: 0}}
                                    transition={{duration: 0.4}}
                                    key={0}
                                    px={['2.5', '5']}
                                    position={'relative'}
                                >
                                    {options &&
                                        <PortfolioListGridView companies={options}/>
                                    }
                                </ListWrapper>
                            ) : (
                                <ListWrapper
                                    key={1}
                                    initial={{opacity: 0}}
                                    animate={{opacity: 1}}
                                    exit={{opacity: 0}}
                                    transition={{duration: 0.4}}
                                    position={'relative'}
                                >
                                    {options &&
                                        <PortfolioListListView companies={options}/>
                                    }
                                </ListWrapper>
                            )}
                        </AnimatePresence>
                        <PortfolioDetail companies={options}/>
                    </Container>
                </Box>
            </Box>
        </>
    );
};

export default PortfolioList;
